import { useCallback } from 'react';

import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { AlertWithReferences } from '@/components/atoms/AlertWithReferences/AlertWithReferences';
import { useAllRules } from '@/hooks/useAllRules';
import useVersions from '@/hooks/useVersions';
import { popModal } from '@/redux/modals/actions';
import { selectAccountSlug, selectBrainId } from '@/redux/session/selectors';
import { Rule } from '@/shared/types/rules';

import ModalDelete from './ModalDelete';
import ModalWarn from './ModalWarn';

type Props = { versionNumber: number };
const ModalVersionDelete = ({ versionNumber }: Props) => {
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);
  const brainId = useSelector(selectBrainId);
  const { deleteVersion, deleteStatus } = useVersions({ brainId });
  const dispatch = useDispatch();
  const { rulesConnected, noRules } = useAllRules({ brainId, brainVersion: versionNumber });

  const handleDelete = useCallback(() => {
    deleteVersion(
      { version: versionNumber },
      {
        onSuccess: () => {
          dispatch(popModal());
        },
      }
    );
  }, [deleteVersion, dispatch, versionNumber]);

  const subtitle = <Trans i18nKey="versions.delete_warning" values={[versionNumber]} />;

  if (noRules) {
    return <ModalDelete subtitle={subtitle} onDelete={handleDelete} />;
  }

  const isDeleting = deleteStatus === 'pending';

  return (
    <ModalWarn isSubmitting={isDeleting}>
      <AlertWithReferences<Partial<Rule>>
        referenceProp="rule_id"
        references={rulesConnected}
        labelKey="name"
        onRefClick={(rule_id) => {
          const deskId = rulesConnected.find((rule) => rule.rule_id === rule_id)?.desk_id;
          dispatch(popModal());
          navigate(`/${slug}/environments/${deskId}/rules/${rule_id}`);
        }}
      >
        <Trans i18nKey={'versions.delete_warning_not_empty'} values={{ 0: versionNumber }} />
      </AlertWithReferences>
    </ModalWarn>
  );
};

export default ModalVersionDelete;
